import { useState } from "react";
import { toast } from "sonner";
import { Folder, FolderPlus, MoreVertical, ChevronRight } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import { Button } from "../ui/button";

export const pathOf = (folders, id) => {
  const out = [];
  let cur = folders.find((f) => f.id === id);
  while (cur) { out.unshift(cur); cur = folders.find((f) => f.id === cur.parent_id); }
  return out;
};

export function FolderSelect({ folders, value, onChange, exclude, tid }) {
  return (
    <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={tid}>
      <option value="">Top level (no folder)</option>
      {folders.filter((f) => f.id !== exclude).map((f) => <option key={f.id} value={f.id}>{pathOf(folders, f.id).map((p) => p.name).join(" / ")}</option>)}
    </select>
  );
}

export function Breadcrumb({ folders, current, onOpen, root }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-sm" data-testid="folder-breadcrumb">
      <button className={current ? "text-[#C2A462] hover:underline" : "font-semibold"} onClick={() => onOpen(null)} data-testid="folder-crumb-root">{root}</button>
      {pathOf(folders, current).map((f) => <span key={f.id} className="flex items-center gap-1"><ChevronRight className="h-3.5 w-3.5 text-slate-500" />
        <button className={f.id === current ? "font-semibold" : "text-[#C2A462] hover:underline"} onClick={() => onOpen(f.id)} data-testid={`folder-crumb-${f.id}`}>{f.name}</button></span>)}
    </div>
  );
}

export function FolderGrid({ kind, scope, folders, current, onOpen, reload }) {
  const [moving, setMoving] = useState(null);
  const kids = folders.filter((f) => (f.parent_id || null) === (current || null));
  const create = async () => {
    const name = window.prompt("Folder name");
    if (!name) return;
    try { await api.post("/folders", { kind, scope, name, parent_id: current }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (f) => { const name = window.prompt("Rename folder", f.name); if (name) { await api.put(`/folders/${f.id}`, { name }); reload(); } };
  const remove = async (f) => { if (window.confirm(`Delete folder "${f.name}"? Anything inside moves up one level.`)) { await api.delete(`/folders/${f.id}`); reload(); } };
  const move = async (to) => { try { await api.put(`/folders/${moving.id}`, { parent_id: to }); setMoving(null); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-1" data-testid={`folders-${kind}`}>
      <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground">Folders</div>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={create} data-testid={`new-folder-${kind}`}><FolderPlus className="h-3.5 w-3.5 mr-1" />New folder</Button></div>
      {moving && <div className="flex items-center gap-2 rounded-md border border-[#C2A462]/30 p-2 text-sm">Move "{moving.name}" to
        <div className="flex-1"><FolderSelect folders={folders} exclude={moving.id} value={moving.parent_id} onChange={move} tid="folder-move-select" /></div>
        <Button size="sm" variant="ghost" onClick={() => setMoving(null)}>Cancel</Button></div>}
      {kids.length > 0 && <div className="rounded-md border border-white/[0.06]">
        {kids.map((f) => (
          <div key={f.id} className="group flex items-center gap-2 px-2 py-1 hover:bg-[#C2A462]/[0.08] transition-colors" data-testid={`folder-${f.id}`}>
            <button className="flex flex-1 items-center gap-2 py-0.5 text-left text-[13px] min-w-0" onClick={() => onOpen(f.id)} data-testid={`folder-open-${f.id}`}><ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-500" /><Folder className="h-4 w-4 shrink-0 fill-[#E9B949] text-[#C9982C]" strokeWidth={1.4} /><span className="truncate">{f.name}</span></button>
            <DropdownMenu><DropdownMenuTrigger asChild><button className="rounded p-0.5 text-slate-500 opacity-60 hover:text-white group-hover:opacity-100" data-testid={`folder-menu-${f.id}`}><MoreVertical className="h-3.5 w-3.5" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => rename(f)} data-testid={`folder-rename-${f.id}`}>Rename</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMoving(f)} data-testid={`folder-move-${f.id}`}>Move to folder</DropdownMenuItem>
                <DropdownMenuItem onClick={() => remove(f)} className="text-red-400" data-testid={`folder-delete-${f.id}`}>Delete</DropdownMenuItem>
              </DropdownMenuContent></DropdownMenu>
          </div>
        ))}
      </div>}
    </div>
  );
}
